import moment from 'moment-timezone';

const NEUTRAL_LEAVE_TYPES = new Set(['short leave', 'work from home']);

const normalizeText = (value) => (value || '').toString().trim();

const normalizeId = (member) => member?.id ?? member?._id ?? '';

const normalizeName = (member) => {
  const explicitName = normalizeText(member?.name);
  if (explicitName) return explicitName;

  const parts = [member?.firstName, member?.lastName].map(normalizeText).filter(Boolean);
  if (parts.length > 0) return parts.join(' ');

  return normalizeText(member?.userName) || 'Unknown';
};

const normalizeEmail = (member) => normalizeText(member?.officialEmail || member?.email);

const normalizeDepartment = (member) => normalizeText(member?.Department || member?.department) || 'N/A';

const normalizeProfilePhotoUrl = (member) => normalizeText(member?.profilePhotoUrl);

const isNeutralLeave = (leave) => NEUTRAL_LEAVE_TYPES.has(normalizeText(leave?.leaveType).toLowerCase());

const formatTime = (value, timeZone) => {
  if (!value) return null;
  return moment(value).tz(timeZone).format('HH:mm');
};

const getDailyRecordForDay = (member, todayKey, timeZone) =>
  member?.dailyHours?.find((day) => moment(day.date).tz(timeZone).format('YYYY-MM-DD') === todayKey) || null;

const getRequiredHours = (member, dayRecord) =>
  Number(
    dayRecord?.requiredDailyHours ||
    member?.workingPolicy?.requiredDailyHours ||
    member?.policy?.requiredDailyHours ||
    member?.expectedDailyHours ||
    member?.stats?.attendance?.requiredDailyHours ||
    8
  );

const getMeaningfulAttendance = (dayRecord) => {
  if (!dayRecord) return false;

  const hoursWorked = Number(dayRecord.hoursWorked || 0) + Number(dayRecord.extraHours || 0);
  return Boolean(dayRecord.firstCheckIn || dayRecord.lastCheckOut || hoursWorked > 0);
};

const getTodaysLeaveState = (member, todayKey, timeZone) => {
  let approvedLeave = null;
  let hasNeutralLeave = false;

  member?.leaveRequests?.forEach((entry) => {
    if ((entry?.status || '').toLowerCase() !== 'approved') return;

    const startDate = moment(entry.startDate).tz(timeZone).format('YYYY-MM-DD');
    const endDate = moment(entry.endDate).tz(timeZone).format('YYYY-MM-DD');
    if (todayKey < startDate || todayKey > endDate) return;

    if (isNeutralLeave(entry)) {
      hasNeutralLeave = true;
      return;
    }

    if (!approvedLeave) {
      approvedLeave = entry;
    }
  });

  return { approvedLeave, hasNeutralLeave };
};

export const buildTodayAttendanceSummary = (teamMembers = [], options = {}) => {
  const timeZone = options.timeZone || 'Asia/Kolkata';
  const todayKey = options.todayKey || moment().format('YYYY-MM-DD');

  const activeToday = [];
  const onLeaveToday = [];
  const absentToday = [];

  teamMembers.forEach((member) => {
    const dayRecord = getDailyRecordForDay(member, todayKey, timeZone);
    const hasAttendance = getMeaningfulAttendance(dayRecord);
    const { approvedLeave, hasNeutralLeave } = getTodaysLeaveState(member, todayKey, timeZone);

    if (hasAttendance) {
      const hoursWorked = Number(dayRecord?.hoursWorked || 0) + Number(dayRecord?.extraHours || 0);
      const requiredHours = getRequiredHours(member, dayRecord);
      const completionPercentage = requiredHours > 0 ? Math.min((hoursWorked / requiredHours) * 100, 100) : 0;
      const profilePhotoUrl = normalizeProfilePhotoUrl(member);

      activeToday.push({
        id: normalizeId(member),
        name: normalizeName(member),
        email: normalizeEmail(member),
        department: normalizeDepartment(member),
        ...(profilePhotoUrl ? { profilePhotoUrl } : {}),
        status: dayRecord?.lastCheckOut ? 'Completed' : 'Active',
        checkIn: formatTime(dayRecord?.firstCheckIn, timeZone),
        checkOut: formatTime(dayRecord?.lastCheckOut, timeZone),
        hoursWorked,
        requiredHours,
        completionPercentage,
        deviceNumber: dayRecord?.deviceNumber || dayRecord?.device || '',
        lastUpdated: dayRecord?.updatedAt || dayRecord?.lastCheckOut || dayRecord?.firstCheckIn || null,
      });
      return;
    }

    if (approvedLeave) {
      const profilePhotoUrl = normalizeProfilePhotoUrl(member);

      onLeaveToday.push({
        id: normalizeId(member),
        name: normalizeName(member),
        email: normalizeEmail(member),
        department: normalizeDepartment(member),
        ...(profilePhotoUrl ? { profilePhotoUrl } : {}),
        leaveType: approvedLeave.leaveType || '',
        leaveStartDate: moment(approvedLeave.startDate).tz(timeZone).format('YYYY-MM-DD'),
        leaveEndDate: moment(approvedLeave.endDate).tz(timeZone).format('YYYY-MM-DD'),
      });
      return;
    }

    if (hasNeutralLeave) {
      return;
    }

    const profilePhotoUrl = normalizeProfilePhotoUrl(member);

    absentToday.push({
      id: normalizeId(member),
      name: normalizeName(member),
      email: normalizeEmail(member),
      department: normalizeDepartment(member),
      ...(profilePhotoUrl ? { profilePhotoUrl } : {}),
    });
  });

  // Sort activeToday by check-in time in ascending order (who checked in first)
  activeToday.sort((a, b) => {
    const timeA = a.checkIn || '';
    const timeB = b.checkIn || '';
    if (!timeA && !timeB) return 0;
    if (!timeA) return 1; // Put members without checkIn time at the end
    if (!timeB) return -1;
    return timeA.localeCompare(timeB);
  });

  return {
    activeToday,
    onLeaveToday,
    absentToday,
    absenteesTotal: onLeaveToday.length + absentToday.length,
  };
};
